/**
 * ReferralDetail — the referral's page, at /referral/:id. ADVISER ONLY: the
 * route is wrapped in AdviserOnlyRoute (App.jsx) and the view refuses any
 * other active role again here, so a deep link never shows a referral to
 * an assistant. Referral cards render inert for the other roles on the
 * board (§30-style app-level gating; RLS untouched).
 *
 * Layout, top to bottom:
 *   - the ReferralCard (display only) beside a meta strip;
 *   - the voice note (audio player) and its AI summary, when recorded;
 *   - the progress track with two tick actions — Contacted and Meeting
 *     booked — each stamping its timestamp and journaling a system note;
 *   - the next-action date, editable (journaled);
 *   - the notepad (ReferralNotes over the shared NotesLog engine);
 *   - action buttons by state: Inbox → Pick up; In Progress → Pause /
 *     Complete; Paused → Resume; Completed → Feedback given (while unset).
 * Every status change goes through referralsApi, which writes the matching
 * system note exactly as pipelineService.pauseFile does for files.
 *
 * The presentational part (ReferralDetailBody) is exported separately from
 * the page (data loading) so it can be rendered with a fixed record.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Loader, Play, Pause, CheckSquare, RotateCcw, ThumbsUp, Check, Undo2,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import ReferralCard from '../components/files/ReferralCard';
import ReferralNotes from '../components/referrals/ReferralNotes';
import ReferralPauseDialog from '../components/referrals/ReferralPauseDialog';
import ReferralCompleteDialog from '../components/referrals/ReferralCompleteDialog';
import {
  getReferralById,
  updateReferralStatus,
  setReferralProgress,
  setReferralNextAction,
  pauseReferral,
  resumeReferral,
  completeReferral,
  markReferralFeedbackGiven,
} from '../api/referralsApi';
import { formatDateZA, formatDateTimeSAST } from '../utils/dateHelpers';
import { returnToDashboard, movedToMessage } from '../utils/returnToDashboard';
import {
  REFERRAL_STATUS_LABELS,
  REFERRAL_CHANNEL_LABELS,
  REFERRAL_OUTCOME_LABELS,
  REFERRER_TYPE_LABELS,
  isReferralOverdue,
  isReferralFeedbackOwed,
} from '../utils/referrals';

const EMAIL_TO_FIRST_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
  'bridgesvix@gmail.com': 'Vicky',
};
const firstNameOf = (email) =>
  EMAIL_TO_FIRST_NAME[(email || '').toLowerCase()] || (email ? email.split('@')[0] : 'User');

const STATUS_COLOURS = {
  inbox: 'var(--cp-secondary)',
  in_progress: 'var(--cp-success)',
  paused: 'var(--cp-warning)',
  completed: 'var(--cp-text)',
};

export default function ReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getReferralById(id);
      setReferral(data);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Referral not found.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (role !== 'adviser') {
    return (
      <div style={styles.outer}>
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>Only the Adviser can open referrals.</span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.outer}>
        <div style={styles.loadingRow}><Loader size={18} className="spin" /> Loading…</div>
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div style={styles.outer}>
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error || 'Referral not found.'}</span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <ReferralDetailBody
      referral={referral}
      onChange={setReferral}
      user={user}
      role={role}
      onBack={() => navigate('/')}
      // Column move succeeded: return to the dashboard with the card
      // highlighted in its new column (CLAUDE.md §39).
      onMoved={(updated) =>
        returnToDashboard(navigate, {
          role,
          cardType: 'referral',
          cardId: updated.id,
          message: movedToMessage(REFERRAL_STATUS_LABELS[updated.pipelineStatus] || updated.pipelineStatus),
        })
      }
    />
  );
}

/**
 * The page body for a loaded referral. `onChange(updated)` receives the
 * record returned by each write so the page updates in place; the board
 * picks the change up on its next refresh.
 */
export function ReferralDetailBody({ referral, onChange, user, role, onBack, onMoved }) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [showPause, setShowPause] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [nextActionDraft, setNextActionDraft] = useState(referral.nextActionOn || '');
  const [notesRefresh, setNotesRefresh] = useState(0);

  useEffect(() => { setNextActionDraft(referral.nextActionOn || ''); }, [referral.nextActionOn]);

  const actor = { id: user?.id, email: user?.email, firstName: firstNameOf(user?.email), role };
  const status = referral.pipelineStatus;
  const isCompleted = status === 'completed';
  const overdue = isReferralOverdue(referral);
  const feedbackOwed = isReferralFeedbackOwed(referral);
  const bumpNotes = () => setNotesRefresh((n) => n + 1);

  const run = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await fn();
      if (updated) onChange && onChange(updated);
      bumpNotes();
      return updated || true;
    } catch (e) {
      setActionError(e?.message || 'That did not work. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Column-changing transitions (pick up, pause, resume, complete) hand the
  // written record to onMoved once the write has succeeded, which returns
  // the user to the dashboard (§39). Progress ticks, the next-action date,
  // feedback-given and note saves use plain `run` and stay on the page.
  const move = async (fn) => {
    const updated = await run(fn);
    if (updated && onMoved) onMoved(updated === true ? referral : updated);
    return updated;
  };

  const pickUp = () => move(() => updateReferralStatus(referral, 'in_progress', actor));
  const resume = () => move(() => resumeReferral(referral, actor));
  const feedbackGiven = () => run(() => markReferralFeedbackGiven(referral, actor));
  const tickContacted = (on) => run(() => setReferralProgress(referral, { contactedAt: on ? true : null }));
  const tickMeeting = (on) => run(() => setReferralProgress(referral, { meetingAt: on ? true : null }));
  const saveNextAction = () => run(() => setReferralNextAction(referral, nextActionDraft));
  const confirmPause = async ({ reason, resumeOn }) => {
    const ok = await move(() => pauseReferral(referral, reason, resumeOn, actor));
    if (ok) setShowPause(false);
  };
  const confirmComplete = async (answers) => {
    const ok = await move(() => completeReferral(referral, answers, actor));
    if (ok) setShowComplete(false);
  };

  const steps = [
    { key: 'contacted', label: 'Contacted', at: referral.progressContactedAt, tick: tickContacted },
    { key: 'meeting', label: 'Meeting booked', at: referral.progressMeetingAt, tick: tickMeeting },
  ];

  return (
    <div style={styles.outer}>
      <div style={styles.topBar}>
        <button className="btn btn-secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={styles.eyebrow}>Referral</div>
        {referral.isTest && <span style={styles.testBadge}>TEST</span>}
      </div>

      {/* Card + meta */}
      <div style={styles.hero}>
        <div style={styles.cardCol}>
          <ReferralCard referral={referral} interactive={false} />
        </div>
        <div style={styles.metaCol}>
          <span style={{ ...styles.statusBadge, background: `${STATUS_COLOURS[status] || 'var(--cp-text-muted)'}`, color: 'white' }}>
            {REFERRAL_STATUS_LABELS[status] || status}
          </span>
          <div style={styles.metaGrid}>
            <MetaItem label="Prospect" value={referral.prospectName} />
            <MetaItem
              label="Referred by"
              value={
                referral.referrer
                  ? `${referral.referrer.displayName} · ${REFERRER_TYPE_LABELS[referral.referrer.referrerType] || ''}${referral.referrer.hasReferralArrangement ? ' · arrangement in place' : ''}`
                  : '—'
              }
            />
            <MetaItem label="Received" value={`${referral.receivedOn ? formatDateZA(referral.receivedOn) : '—'} via ${REFERRAL_CHANNEL_LABELS[referral.channel] || referral.channel || '—'}`} />
            {referral.reLine && <MetaItem label="Re" value={referral.reLine} />}
            {isCompleted && (
              <MetaItem
                label="Outcome"
                value={`${REFERRAL_OUTCOME_LABELS[referral.outcome] || '—'}${referral.completedAt ? ` · closed ${formatDateZA(referral.completedAt)}` : ''}${referral.feedbackGivenAt ? ` · feedback given ${formatDateZA(referral.feedbackGivenAt)}` : ''}`}
              />
            )}
            {isCompleted && referral.linkedFileId && (
              <MetaItem label="Client file" value={<a href={`/file/${referral.linkedFileId}`}>Open linked file →</a>} />
            )}
          </div>

          {/* Next action — editable while the referral is open */}
          {!isCompleted && (
            <div style={styles.nextAction}>
              <label htmlFor="ref-next-action" style={styles.metaLabel}>
                {status === 'paused' ? 'Resume on' : 'Next action'}
                {overdue && <span style={styles.overdueTag}>{status === 'paused' ? 'RESUME DUE' : 'OVERDUE'}</span>}
              </label>
              <div style={styles.nextActionRow}>
                <input
                  id="ref-next-action"
                  type="date"
                  value={nextActionDraft}
                  onChange={(e) => setNextActionDraft(e.target.value)}
                  style={styles.dateInput}
                  disabled={busy}
                />
                <button
                  className="btn btn-outline"
                  onClick={saveNextAction}
                  disabled={busy || !nextActionDraft || nextActionDraft === referral.nextActionOn}
                >
                  Save date
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Voice note + summary */}
      {(referral.voiceNoteUrl || referral.voiceSummary) && (
        <div className="card" style={styles.section}>
          <h3 style={styles.sectionTitle}>Voice note</h3>
          {referral.voiceNoteUrl && (
            <audio controls src={referral.voiceNoteUrl} style={styles.audio}>
              Your browser does not support audio playback.
            </audio>
          )}
          {referral.voiceSummary && (
            <>
              <div style={styles.summaryLabel}>AI summary</div>
              <pre style={styles.summary}>{referral.voiceSummary}</pre>
            </>
          )}
        </div>
      )}

      {/* Progress track */}
      <div className="card" style={styles.section}>
        <h3 style={styles.sectionTitle}>Progress</h3>
        <div style={styles.steps}>
          {steps.map((s) => (
            <div key={s.key} style={styles.step}>
              <button
                type="button"
                style={{ ...styles.tick, ...(s.at ? styles.tickOn : {}) }}
                onClick={() => s.tick(!s.at)}
                disabled={busy || isCompleted}
                aria-pressed={!!s.at}
                aria-label={s.at ? `${s.label} — clear` : `Mark ${s.label.toLowerCase()}`}
                title={s.at ? 'Click to clear' : undefined}
              >
                {s.at ? <Check size={16} /> : null}
              </button>
              <div>
                <div style={styles.stepLabel}>{s.label}</div>
                <div style={styles.stepMeta}>{s.at ? formatDateTimeSAST(s.at) : 'Not yet'}</div>
              </div>
            </div>
          ))}
          <div style={styles.step}>
            <span style={{ ...styles.tick, ...(isCompleted ? styles.tickOn : {}), cursor: 'default' }}>
              {isCompleted ? <Check size={16} /> : null}
            </span>
            <div>
              <div style={styles.stepLabel}>Outcome</div>
              <div style={styles.stepMeta}>
                {isCompleted ? REFERRAL_OUTCOME_LABELS[referral.outcome] || 'Completed' : 'On completion'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {actionError && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {/* Actions by state */}
      <div style={styles.actions}>
        {status === 'inbox' && (
          <button className="btn btn-primary" onClick={pickUp} disabled={busy}>
            <Play size={16} /> Pick up
          </button>
        )}
        {status === 'in_progress' && (
          <>
            <button className="btn btn-accent" onClick={() => setShowPause(true)} disabled={busy}>
              <Pause size={16} /> Pause
            </button>
            <button className="btn btn-primary" onClick={() => setShowComplete(true)} disabled={busy}>
              <CheckSquare size={16} /> Complete
            </button>
          </>
        )}
        {status === 'paused' && (
          <button className="btn btn-primary" onClick={resume} disabled={busy}>
            <RotateCcw size={16} /> Resume
          </button>
        )}
        {isCompleted && feedbackOwed && (
          <button className="btn btn-primary" onClick={feedbackGiven} disabled={busy}>
            <ThumbsUp size={16} /> Feedback given
          </button>
        )}
        {isCompleted && !feedbackOwed && referral.feedbackGivenAt && (
          <span style={styles.doneNote}><Undo2 size={14} style={{ visibility: 'hidden' }} />Loop closed — feedback given {formatDateZA(referral.feedbackGivenAt)}.</span>
        )}
      </div>

      {/* Notepad */}
      <ReferralNotes
        referralId={referral.id}
        refreshSignal={notesRefresh}
        currentUser={{ id: user?.id || null, displayName: firstNameOf(user?.email) }}
        currentRole={role}
      />

      <ReferralPauseDialog
        open={showPause}
        referral={referral}
        busy={busy}
        onCancel={() => setShowPause(false)}
        onConfirm={confirmPause}
      />
      <ReferralCompleteDialog
        open={showComplete}
        referral={referral}
        busy={busy}
        onCancel={() => setShowComplete(false)}
        onConfirm={confirmComplete}
      />
    </div>
  );
}

function MetaItem({ label, value }) {
  return (
    <div style={styles.metaItem}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value}</div>
    </div>
  );
}

const styles = {
  outer: { maxWidth: '760px', margin: '0 auto', padding: '0.5rem 0 2rem' },
  loadingRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--cp-text-muted)', padding: '2rem 0' },
  topBar: { display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' },
  eyebrow: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--cp-text-muted)' },
  testBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '8px',
    background: 'var(--cp-surface-alt)',
    border: '1px dashed var(--cp-warning)',
    color: 'var(--cp-warning)',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(231, 111, 81, 0.12)',
    border: '1px solid var(--cp-warning)',
    color: 'var(--cp-danger)',
    borderRadius: '10px',
    padding: '0.6rem 0.9rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  hero: { display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '1rem' },
  cardCol: { flex: '0 0 250px', width: '250px' },
  metaCol: { flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '18px' },
  statusBadge: {
    alignSelf: 'flex-start',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '0.15rem 0.6rem',
    borderRadius: '999px',
  },
  metaGrid: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  metaItem: {},
  metaLabel: {
    fontSize: '9.5px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'var(--cp-referral-ink)',
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  metaValue: { fontSize: '0.9rem', color: 'var(--cp-text)' },
  nextAction: { marginTop: '0.25rem' },
  nextActionRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' },
  dateInput: { width: 'auto', maxWidth: '190px' },
  overdueTag: {
    fontSize: '9px',
    fontWeight: 800,
    letterSpacing: '0.06em',
    color: 'white',
    background: 'var(--cp-danger)',
    borderRadius: '3px',
    padding: '1px 6px',
  },
  section: { marginBottom: '1rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 700, color: 'var(--cp-primary)', marginBottom: '0.75rem' },
  audio: { width: '100%', marginBottom: '0.75rem' },
  summaryLabel: { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--cp-text-muted)', marginBottom: '0.35rem' },
  summary: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'var(--font-ui)',
    fontSize: '0.85rem',
    lineHeight: 1.5,
    background: 'var(--cp-bg)',
    borderRadius: '10px',
    padding: '0.75rem 0.9rem',
    margin: 0,
    color: 'var(--cp-text)',
  },
  steps: { display: 'flex', flexWrap: 'wrap', gap: '1.25rem' },
  step: { display: 'flex', alignItems: 'center', gap: '0.6rem', flex: '1 1 180px' },
  tick: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '2px solid var(--cp-referral-ink)',
    background: 'var(--cp-surface)',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    flex: '0 0 30px',
  },
  tickOn: { background: 'var(--cp-referral-ink)' },
  stepLabel: { fontSize: '0.85rem', fontWeight: 700, color: 'var(--cp-text)' },
  stepMeta: { fontSize: '0.75rem', color: 'var(--cp-text-muted)' },
  actions: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' },
  doneNote: { fontSize: '0.85rem', color: 'var(--cp-text-muted)', display: 'inline-flex', alignItems: 'center', gap: '4px' },
};
